import _ from "lodash"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router"

import { useTranslation } from "providers/i18n"
import { useVenue } from "providers/venue"
import { useAppUI } from "providers/ui"

import { listItemPropsParserFactory } from "utils/feature"

import { pushDataLayer } from "utils/googleTagManager"
import config from "config"

export const useSearchContainer = ({
  showOnscreenKeyboard: showOnscreenKeyboardProp = undefined,
}) => {
  const { t } = useTranslation({ defaultValue: null })
  const navigate = useNavigate()
  const { search, getSuggestedSearchItems, isMultiOrdinalVenue } = useVenue()
  const { openSearchState, openSearch, closeSearch } = useAppUI()
  const [searchValue, setSearchValue] = useState<string>()
  const [venueFilter, setVenueFilter] = useState<string>()

  const searchInputRef = useRef()

  // To open the On-Screen Keyboard
  const device = config("device")
  const isKiosk = /^kiosk/.test(device)
  const showOnscreenKeyboard = _.isBoolean(showOnscreenKeyboardProp)
    ? showOnscreenKeyboardProp
    : isKiosk

  // Implement Search With Custom Debounce function
  const debounceSearchOnChange = useMemo(
    () =>
      _.debounce((value) => {
        setSearchValue(value)
        if (value.trim() !== "") {
          pushDataLayer({
            event: "search_keyword",
            keyword: value,
          })
        }
      }, 400), // Debounce for 400 milliseconds
    []
  )

  // Search Result
  const resultParser = listItemPropsParserFactory({
    t,
    hideLevel: !isMultiOrdinalVenue,
  })

  const searchResultItems = useMemo(() => {
    const features = !_.isEmpty(searchValue)
      ? search(searchValue, venueFilter)
      : getSuggestedSearchItems(venueFilter)
    return features.map(resultParser)
  }, [searchValue, search, getSuggestedSearchItems, resultParser, venueFilter])

  // Events
  const onChange = useCallback(
    (event) => {
      const newValue = event.target.value
      if (searchInputRef.current)
        (searchInputRef.current as HTMLInputElement).value = newValue
      debounceSearchOnChange(newValue)
    },
    [debounceSearchOnChange]
  )

  const onChangeKeyboard = useCallback(
    (value) => {
      if (searchInputRef.current)
        (searchInputRef.current as HTMLInputElement).value = value
      debounceSearchOnChange(value)
    },
    [debounceSearchOnChange]
  )

  const clearSearchValue = useCallback(() => {
    if (searchInputRef.current)
      (searchInputRef.current as HTMLInputElement).value = ""
    setSearchValue("")
  }, [setSearchValue])

  const reset = useCallback(() => {
    clearSearchValue()
    closeSearch()
  }, [closeSearch, clearSearchValue])

  const onSelectResult = useCallback(
    (feature) => {
      reset()
      navigate(`/maps/place/${feature.id}`)
    },
    [navigate, reset]
  )

  // Effect
  useEffect(() => {
    window.addEventListener("appreset", () => {
      reset()
    })
  }, [reset])

  return {
    // Input ref
    searchInputRef,
    // States
    searchResultItems,
    searchValue,
    openSearchState,
    showOnscreenKeyboard,
    venueFilter,
    // Functions
    openSearch,
    setSearchValue,
    onSelectResult,
    onChange,
    onChangeKeyboard,
    reset,
    clearSearchValue,
    setVenueFilter,
  }
}
